import { Check, ChevronsUpDown, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { currentFiscalYear, formatFY } from "@/lib/fiscal";
import { cn } from "@/lib/utils";
import {
  AddGoalSelect,
  AFF_METRICS,
  CopyToFYSelect,
  type GoalRow,
  GoalRowInput,
  getMetricDef,
  SectionHeader,
  useSaveFlash,
} from "./shared";

export function AffiliateGoalsSection({
  ownerId,
  fiscalYear,
  onDataChange,
  onRemove,
  readOnly = false,
}: {
  ownerId: string;
  fiscalYear: number;
  onDataChange: (hasData: boolean) => void;
  onRemove?: () => void;
  readOnly?: boolean;
}) {
  const currentFY = currentFiscalYear();
  const [goals, setGoals] = useState<GoalRow[]>([]);
  const [newMetricKey, setNewMetricKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { savedField, flashSaved } = useSaveFlash();
  const lastSavedRef = useRef<Record<string, string>>({});

  // Exclusion list state
  const [exclusions, setExclusions] = useState<string[]>([]);
  const [allAffiliateNames, setAllAffiliateNames] = useState<string[]>([]);
  const [exclusionPopoverOpen, setExclusionPopoverOpen] = useState(false);

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/owners/${ownerId}/targets?fy=${fiscalYear}`);
      if (res.ok) {
        const data = await res.json();
        const targets = data.targets as { metric: string; targetValue: number }[];
        const rows: GoalRow[] = targets
          .filter((t) => AFF_METRICS.some((m) => m.key === t.metric))
          .map((t) => ({ metric: t.metric, value: String(t.targetValue) }));
        setGoals(rows);

        const snapshot: Record<string, string> = {};
        for (const r of rows) snapshot[r.metric] = r.value;
        lastSavedRef.current = snapshot;
        onDataChange(rows.length > 0);
      }
    } finally {
      setLoading(false);
    }
  }, [ownerId, fiscalYear, onDataChange]);

  const fetchExclusions = useCallback(async () => {
    const res = await fetch(`/api/owners/${ownerId}/affiliate-exclusions?fy=${fiscalYear}`);
    if (res.ok) {
      const data = await res.json();
      setExclusions(data.exclusions ?? []);
    }
  }, [ownerId, fiscalYear]);

  const fetchAffiliateNames = useCallback(async () => {
    const res = await fetch("/api/deals/affiliate-names");
    if (res.ok) {
      const data = await res.json();
      setAllAffiliateNames(data.values ?? []);
    }
  }, []);

  useEffect(() => {
    fetchTargets();
    fetchExclusions();
    fetchAffiliateNames();
  }, [fetchTargets, fetchExclusions, fetchAffiliateNames]);

  const usedMetrics = new Set(goals.map((g) => g.metric));
  const availableToAdd = AFF_METRICS.filter((m) => !usedMetrics.has(m.key));

  const sortedGoals = [...goals].sort((a, b) => {
    if (a.metric === newMetricKey) return 1;
    if (b.metric === newMetricKey) return -1;
    return (getMetricDef(a.metric)?.label ?? "").localeCompare(getMetricDef(b.metric)?.label ?? "");
  });

  function addGoal(metricKey: string) {
    setGoals((prev) => [...prev, { metric: metricKey, value: "" }]);
    setNewMetricKey(metricKey);
  }

  function updateGoalValue(metric: string, value: string) {
    setGoals((prev) => prev.map((g) => (g.metric === metric ? { ...g, value } : g)));
  }

  const saveMetric = useCallback(
    async (metric: string, value: string) => {
      if (value === (lastSavedRef.current[metric] ?? "")) return;

      const res = await fetch(`/api/owners/${ownerId}/targets`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fiscalYear,
          targets: [{ metric, targetValue: value === "" ? null : Number(value) }],
        }),
      });

      if (res.ok) {
        lastSavedRef.current[metric] = value;
        if (newMetricKey === metric) setNewMetricKey(null);
        flashSaved(metric);
        const hasData = goals.some((g) => g.metric !== metric && g.value !== "") || value !== "";
        onDataChange(hasData);
      }
    },
    [ownerId, fiscalYear, newMetricKey, flashSaved, goals, onDataChange],
  );

  function handleBlur(metric: string) {
    const goal = goals.find((g) => g.metric === metric);
    if (goal) saveMetric(metric, goal.value);
  }

  function removeGoal(metric: string) {
    setGoals((prev) => {
      const updated = prev.filter((g) => g.metric !== metric);
      onDataChange(updated.some((g) => g.value !== ""));
      return updated;
    });
    fetch(`/api/owners/${ownerId}/targets`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fiscalYear, targets: [{ metric, targetValue: null }] }),
    });
    delete lastSavedRef.current[metric];
  }

  async function removeAll() {
    const toDelete = goals.map((g) => ({ metric: g.metric, targetValue: null }));
    if (toDelete.length > 0) {
      await fetch(`/api/owners/${ownerId}/targets`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fiscalYear, targets: toDelete }),
      });
    }
    // Also clear exclusions
    await fetch(`/api/owners/${ownerId}/affiliate-exclusions`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fiscalYear, exclusions: [] }),
    });
    setGoals([]);
    setExclusions([]);
    lastSavedRef.current = {};
    onDataChange(false);
    onRemove?.();
  }

  async function copyToFY(targetFY: number) {
    const filled = goals.filter((g) => g.value !== "");
    if (filled.length === 0) return;
    await fetch(`/api/owners/${ownerId}/targets`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fiscalYear: targetFY,
        targets: filled.map((g) => ({ metric: g.metric, targetValue: Number(g.value) })),
      }),
    });
    // Also copy exclusions to target FY
    if (exclusions.length > 0) {
      await fetch(`/api/owners/${ownerId}/affiliate-exclusions`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fiscalYear: targetFY, exclusions }),
      });
    }
  }

  async function saveExclusions(updated: string[]) {
    setExclusions(updated);
    await fetch(`/api/owners/${ownerId}/affiliate-exclusions`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fiscalYear, exclusions: updated }),
    });
  }

  function toggleExclusion(name: string) {
    const updated = exclusions.includes(name)
      ? exclusions.filter((n) => n !== name)
      : [...exclusions, name];
    saveExclusions(updated);
  }

  function removeExclusion(name: string) {
    saveExclusions(exclusions.filter((n) => n !== name));
  }

  return (
    <Card>
      <CardContent className="pt-5">
        <SectionHeader title="Affiliate Goals" onRemove={readOnly ? undefined : removeAll}>
          {!readOnly && goals.length > 0 && (
            <CopyToFYSelect currentFY={currentFY} selectedFY={fiscalYear} onCopy={copyToFY} />
          )}
        </SectionHeader>

        <div className={loading ? "opacity-60" : ""}>
          {goals.length === 0 && !loading ? (
            <p className="text-sm text-muted-foreground py-2">
              No affiliate targets configured for {formatFY(fiscalYear)}.
              {!readOnly && " Add a metric target below."}
            </p>
          ) : (
            <div className="space-y-3">
              {sortedGoals.map((goal) => (
                <GoalRowInput
                  key={goal.metric}
                  goal={goal}
                  autoFocus={goal.metric === newMetricKey}
                  onChangeValue={(v) => updateGoalValue(goal.metric, v)}
                  onBlur={() => handleBlur(goal.metric)}
                  onRemove={readOnly ? undefined : () => removeGoal(goal.metric)}
                  disabled={readOnly}
                  savedField={savedField}
                />
              ))}
            </div>
          )}

          {!readOnly && <AddGoalSelect availableMetrics={availableToAdd} onAdd={addGoal} />}

          {/* Exclusion list */}
          <Separator className="my-4" />
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Excluded Affiliate Names</p>
            <p className="text-xs text-muted-foreground">
              Deals with these affiliate names will not count toward affiliate goals. All other
              non-empty affiliate names are included.
            </p>

            {exclusions.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {exclusions.map((name) => (
                  <Badge
                    key={name}
                    variant="secondary"
                    className={cn("text-xs gap-1", !readOnly && "pr-1")}
                  >
                    {name}
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => removeExclusion(name)}
                        className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </Badge>
                ))}
              </div>
            )}

            {!readOnly && (
              <Popover open={exclusionPopoverOpen} onOpenChange={setExclusionPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs w-full justify-between"
                  >
                    Add exclusion...
                    <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search affiliate names..." />
                    <CommandList>
                      <CommandEmpty>No affiliate names found.</CommandEmpty>
                      <CommandGroup>
                        {allAffiliateNames.map((name) => (
                          <CommandItem
                            key={name}
                            value={name}
                            onSelect={() => toggleExclusion(name)}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                exclusions.includes(name) ? "opacity-100" : "opacity-0",
                              )}
                            />
                            {name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
